import React, { useCallback, useEffect, useRef, useState } from "react";
import * as uuid from "uuid";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import {
  fetchComponentInstanceDetails,
  fetchDocumentPartVersion,
  invalidateDocumentPartVersionCache,
  triggerComponentMappingElaboration,
} from "../../api/document/DocumentsPart";
import { fetchDocumentPath } from "../../api/document/Documents";
import { ComponentSourceGroup } from "../../api/models/component-source-group";
import { ComponentSourceMappingPair } from "../../api/models/component-source-mapping-pair";
import { InstanceHint } from "../../api/models/instance-hint";
import { DocumentPart } from "../../api/models";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { APPLY_NOTIFICATION } from "../../utils/helpers/ComponentMappingNotification";
import {
  startComponentMappingLoading,
  stopComponentMappingLoading,
} from "../../utils/helpers/ComponentMappingLoading";
import NotificationManager from "../../common/notification/NotificationManager";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import FooterPanel from "../layout/FooterPanel";
import HeaderPanel from "../layout/HeaderPanel";
import { OperationCompletedPanel } from "../layout/OperationCompletedPanel";
import { ComponentSourceList } from "./component-source/ComponentSourceList";
import { ComponentSourcePicker } from "./component-source/ComponentSourcePicker";
import { SourceListSkeleton } from "./component-source/SourceListSkeleton";
import { PendingSourceChange, usePendingSourceChanges } from "./component-source/usePendingSourceChanges";
import {
  CAPTION_APPLY,
  CAPTION_CHANGE_COMPONENT_SOURCE_LEGACY_INSTANCES,
  CAPTION_CHANGE_COMPONENT_SOURCE_NO_INSTANCES,
  CAPTION_CHANGE_COMPONENT_SOURCE_OUTDATED_VERSION,
  CAPTION_COMPONENT_MAPPING_APPLY_FAILED_MESSAGE,
  CAPTION_COMPONENT_MAPPING_APPLY_FAILED_TITLE,
  CAPTION_OUTLINE_CHANGE_COMPONENT_SOURCE,
} from "./outlineCaptions";

// Keyed by the card's displayed documentId, exactly how usePendingSourceChanges stages them.
const buildSourceMappings = (pendingChanges: Record<string, PendingSourceChange>): ComponentSourceMappingPair[] =>
  Object.entries(pendingChanges).map(([currentSourceDocumentId, replacement]) => ({
    currentSourceDocumentId,
    newSourceDocumentId: replacement.documentId,
  }));

// Every instance in every group, not only the ones staged for remapping - filtering this would silently drop
// the UNMAPPED_NEW_INSTANCE warning server-side.
const buildInstanceHints = (groups: ComponentSourceGroup[]): InstanceHint[] =>
  groups.flatMap((group) =>
    group.instances.map((instance) => ({
      instanceId: instance.instanceId,
      contentControlId: instance.contentControlId,
      instanceDocumentId: instance.instanceDocumentId ?? "",
      currentSourceDocumentId: group.documentId,
    }))
  );

const CAPTION_GENERIC_ERROR = "cof.base.message.genericerror";
const CAPTION_RETRY = "cof.base.retry";
const CAPTION_BACK = "cof.base.back";

const NO_INSTANCES_IMAGE = "/assets/illustrations/notFound.svg";
const ERROR_IMAGE = "/assets/illustrations/oopsNotTagetik.svg";

// A fresh object literal here would fail excess-property checking against AxiosCustomParams, which does not
// declare notShowError - assigning it to a const first sidesteps that check.
const silentPathRequestConfig = { params: { notShowLoading: true, notShowError: true } };

// Order carries no meaning here - the derivation order that matters lives in loadComponentInstances below.
type ChangeComponentSourceViewState = "loading" | "list" | "legacy" | "empty" | "error" | "outdated";

export interface ChangeComponentSourceProps {
  selectedTreeItem: FlatPathTreeItem;
  // Id of the outline root document. The component-instance lookup is defined per root document only, so callers
  // must supply it explicitly rather than letting this component derive it from selectedTreeItem, which could
  // otherwise reference a selected document part instead of the root.
  rootDocumentId: string;
  onBackButtonClick: () => void;
}

export const ChangeComponentSource: React.FunctionComponent<ChangeComponentSourceProps> = (props) => {
  // Always query with the explicit root id - never re-derive it from selectedTreeItem, which is used below only
  // for the header label.
  const documentId = props.rootDocumentId;
  const [viewState, setViewState] = useState<ChangeComponentSourceViewState>("loading");
  const [groups, setGroups] = useState<ComponentSourceGroup[]>([]);
  const [pathsByDocumentId, setPathsByDocumentId] = useState<Record<string, string>>({});
  const [openGroup, setOpenGroup] = useState<ComponentSourceGroup | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const requestIdRef = useRef(0);
  const { pendingChanges, hasPendingChanges, stageChange, applyResolvedPath, revertChange, clearPendingChanges } =
    usePendingSourceChanges();

  const loadComponentInstances = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setViewState("loading");
    setPathsByDocumentId({});
    clearPendingChanges();

    try {
      const documentPartVersion = await fetchDocumentPartVersion(documentId);
      if (requestId !== requestIdRef.current) {
        return;
      }

      // A mismatched echo is neither evidence of an outdated document nor a valid response for this page.
      // Fail closed before the expensive instance endpoint is allowed to run.
      if (documentPartVersion.documentPartId !== documentId) {
        setViewState("error");
        return;
      }

      // The detailed instance endpoint downloads and Aspose-parses the root document. An outdated metadata
      // version cannot use this workflow, so stop here rather than paying that server-side cost.
      if (!documentPartVersion.postRedesign) {
        setViewState("outdated");
        return;
      }
    } catch (error) {
      console.error("Failed to load document part version", { documentId, error });
      if (requestId === requestIdRef.current) {
        setViewState("error");
      }
      return;
    }

    try {
      const result = await fetchComponentInstanceDetails(documentId);
      if (requestId !== requestIdRef.current) {
        return;
      }

      const resultGroups = result.groups ?? [];
      setGroups(resultGroups);

      if (!result.hasInstances) {
        setViewState("empty");
      } else if (result.containsLegacyInstances) {
        setViewState("legacy");
      } else if (resultGroups.length > 0) {
        // Fetched together at the page level (not per card) so failures can be isolated: UNAVAILABLE groups
        // are skipped since they have no document to query, and inaccessible sources legitimately have no path.
        const pathEntries = await Promise.all(
          resultGroups
            .filter((group) => group.resolution !== "UNAVAILABLE")
            .map((group) =>
              fetchDocumentPath(group.documentId, silentPathRequestConfig)
                .catch(() => undefined)
                .then((path) => [group.documentId, path] as const)
            )
        );
        if (requestId !== requestIdRef.current) {
          return;
        }

        const nextPathsByDocumentId: Record<string, string> = {};
        for (const [sourceDocumentId, path] of pathEntries) {
          // The endpoint's empty string is a valid library-root path. Only failures and non-string payloads are
          // omitted; a truthiness check here would incorrectly hide documents stored directly in the library.
          if (typeof path === "string") {
            nextPathsByDocumentId[sourceDocumentId] = path;
          }
        }
        setPathsByDocumentId(nextPathsByDocumentId);
        setViewState("list");
      } else {
        console.warn("hasInstances is true but groups is missing or empty and containsLegacyInstances is not true", {
          documentId,
        });
        setViewState("empty");
      }
    } catch (error) {
      console.error("Failed to load component instances", { documentId, error });
      if (requestId === requestIdRef.current) {
        setViewState("error");
      }
    }
  }, [documentId, clearPendingChanges]);

  useEffect(() => {
    void loadComponentInstances();

    return () => {
      requestIdRef.current++;
      // Invalidate on departure so returning after migrating away from the outdated state re-checks the
      // version instead of reusing stale data.
      invalidateDocumentPartVersionCache(documentId);
    };
  }, [loadComponentInstances]);

  const closePicker = () => setOpenGroup(null);

  const handleConfirm = (documentPart: DocumentPart) => {
    const groupDocumentId = openGroup.documentId;

    if (documentPart.id === groupDocumentId) {
      revertChange(groupDocumentId);
      closePicker();
      return;
    }

    stageChange(groupDocumentId, { id: documentPart.id, name: documentPart.name });
    closePicker();

    const requestId = requestIdRef.current;
    fetchDocumentPath(documentPart.id, silentPathRequestConfig)
      .catch(() => undefined)
      .then((path) => {
        if (requestId !== requestIdRef.current || typeof path !== "string") {
          return;
        }
        applyResolvedPath(groupDocumentId, documentPart.id, path);
      });
  };

  const handleApply = async () => {
    if (!hasPendingChanges || isApplying) {
      return;
    }

    setIsApplying(true);
    const correlationId = uuid.v4();
    startComponentMappingLoading(correlationId);
    try {
      await triggerComponentMappingElaboration(
        documentId,
        buildSourceMappings(pendingChanges),
        buildInstanceHints(groups),
        correlationId
      );
      props.onBackButtonClick();
    } catch (error) {
      stopComponentMappingLoading(correlationId);
      console.error("Failed to request a component mapping", { documentId, error });
      NotificationManager.addNotification({
        key: APPLY_NOTIFICATION,
        title: formatMessage(CAPTION_COMPONENT_MAPPING_APPLY_FAILED_TITLE),
        message: formatMessage(CAPTION_COMPONENT_MAPPING_APPLY_FAILED_MESSAGE),
        type: "error",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
      setIsApplying(false);
    }
  };

  const renderContent = () => {
    if (viewState === "loading") {
      // SourceListSkeleton owns the "change-component-source-loading" test id and the accessible busy state
      // itself, so nothing else wraps it.
      return <SourceListSkeleton />;
    }

    if (viewState === "outdated") {
      // A determined compatibility state, not a transport error: reuse the not-found illustration and pass no
      // children so OperationCompletedPanel cannot render a Retry control.
      const outdatedMessage = formatMessage(CAPTION_CHANGE_COMPONENT_SOURCE_OUTDATED_VERSION);
      return (
        <div data-testid="change-component-source-outdated">
          <OperationCompletedPanel message={outdatedMessage} alt={outdatedMessage} icon={NO_INSTANCES_IMAGE} />
        </div>
      );
    }

    if (viewState === "list") {
      return (
        <div data-testid="change-component-source-list">
          <ComponentSourceList
            groups={groups}
            pathsByDocumentId={pathsByDocumentId}
            pendingChanges={pendingChanges}
            onSearchClick={(group) => setOpenGroup(group)}
            onUndoChange={(group) => revertChange(group.documentId)}
          />
        </div>
      );
    }

    if (viewState === "legacy") {
      // Any single legacy instance replaces the ENTIRE document list, never rendered per group. Not an error:
      // reuses NO_INSTANCES_IMAGE (never ERROR_IMAGE) and passes no children, so OperationCompletedPanel renders
      // no Retry control.
      const legacyMessage = formatMessage(CAPTION_CHANGE_COMPONENT_SOURCE_LEGACY_INSTANCES);
      return (
        <div data-testid="change-component-source-legacy">
          <OperationCompletedPanel message={legacyMessage} alt={legacyMessage} icon={NO_INSTANCES_IMAGE} />
        </div>
      );
    }

    if (viewState === "empty") {
      const noInstancesMessage = formatMessage(CAPTION_CHANGE_COMPONENT_SOURCE_NO_INSTANCES);
      return (
        <div data-testid="change-component-source-empty">
          <OperationCompletedPanel message={noInstancesMessage} alt={noInstancesMessage} icon={NO_INSTANCES_IMAGE} />
        </div>
      );
    }

    // viewState === "error"
    const errorMessage = formatMessage(CAPTION_GENERIC_ERROR);
    return (
      <div data-testid="change-component-source-error">
        <OperationCompletedPanel message={errorMessage} alt={errorMessage} icon={ERROR_IMAGE}>
          <PrimaryButton
            className={ButtonVariants.primary}
            data-testid="change-component-source-retry"
            text={formatMessage(CAPTION_RETRY)}
            onClick={() => void loadComponentInstances()}
          />
        </OperationCompletedPanel>
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        marginBottom: "-2.5rem",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <HeaderPanel data-testid="header" applyPadding>
          {formatMessage(CAPTION_OUTLINE_CHANGE_COMPONENT_SOURCE) + " - " + props.selectedTreeItem.text}
        </HeaderPanel>
      </div>
      <div
        style={{
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
          paddingBottom: "110px",
          // Repository-standard horizontal body inset, matching every other panel, so the cards and each
          // OperationCompletedPanel-based state below sit inset from the task-pane edges instead of flush.
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        {renderContent()}
      </div>
      {openGroup && <ComponentSourcePicker onCancel={closePicker} onSelect={handleConfirm} />}
      <FooterPanel>
        <PrimaryButton
          className={ButtonVariants.primary}
          data-testid="change-component-source-back"
          text={formatMessage(CAPTION_BACK)}
          onClick={props.onBackButtonClick}
          style={{ float: "left" }}
        />
        {/* Wired to handleApply: submits every staged replacement plus the full instance list as one
            COMPONENT_MAPPING elaboration item. */}
        <PrimaryButton
          className={ButtonVariants.primary}
          data-testid="change-component-source-apply"
          disabled={!hasPendingChanges || isApplying}
          text={formatMessage(CAPTION_APPLY)}
          onClick={() => void handleApply()}
          style={{ float: "right" }}
        />
      </FooterPanel>
    </div>
  );
};
